/**
 * SageWidget — floating bottom-right chat launcher + collapsible chat panel.
 *
 * Why this exists:
 *   Jobright.ai ships an always-on "Orion" copilot persona that follows the
 *   user across the app. Their CareerCoach equivalent. We already have the
 *   full Sage backend (/api/career-coach/chat) and the /coach page UI; this
 *   widget makes Sage feel ambient — present on every page, one click away,
 *   rather than buried behind a sidebar link.
 *
 * Behavior:
 *   - Only renders for authenticated users
 *   - Hidden on the /coach route itself (would be redundant)
 *   - Hidden on landing/auth/legal routes — those are still pre-funnel
 *   - Collapsed = floating action button (FAB) in bottom-right
 *   - Expanded = compact chat panel; sends to the same /career-coach/chat
 *     endpoint with a `widget=1` flag for telemetry
 *   - State is session-only; closing the panel doesn't lose the session
 *     until next page reload (keeping the in-flight context warm)
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, Sparkles, X, Loader2, Maximize2, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiBase';

// Routes where Sage SHOULDN'T appear — pre-funnel pages with their own
// CTAs, transactional flows, or the full /coach page itself.
//
// Sage now renders for BOTH authenticated users and anonymous visitors,
// so the suppression list is the only thing keeping it off pages where
// it would be redundant or visually noisy.
const SUPPRESSED_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/auth/',
  '/verify-email',
  '/unsubscribe',
  '/privacy',
  '/terms',
  '/score/',
  '/coach',
  '/sage',           // dedicated Sage landing — already a giant CTA-to-Sage page
];

const isSuppressed = (pathname) => {
  return SUPPRESSED_PREFIXES.some((p) => pathname.startsWith(p));
};

// Quick-prompt sets — different for anon vs authenticated. Anon prompts
// are intentionally PRODUCT-FOCUSED (pricing, features, comparison) so
// the visitor experiences Sage answering questions it CAN answer, not
// hitting the personalization deflection on their first turn.
const ANON_QUICK_PROMPTS = [
  'How much does Ascend cost?',
  'What does Sage do?',
  'How is this different from Jobright?',
];
const AUTH_QUICK_PROMPTS = [
  'What should I fix on my resume this week?',
  'How do I follow up on an application from 8 days ago?',
  'Rewrite my LinkedIn headline.',
];

// Stable per-browser visitor ID for anon rate-limiting. Stored in
// localStorage; rotates per browser, not per session.
const getOrCreateVisitorId = () => {
  if (typeof window === 'undefined') return null;
  try {
    let id = window.localStorage.getItem('sage_visitor_id');
    if (!id) {
      id = `vis_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem('sage_visitor_id', id);
    }
    return id;
  } catch (_e) {
    return null;
  }
};

const SageWidget = () => {
  const { user, loading: authLoading } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll-to-bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  // Auto-focus input on open.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  if (authLoading) return null;
  if (isSuppressed(pathname)) return null;

  // Anonymous = no authenticated user. Anon mode hits a different
  // endpoint with a tightly-scoped system prompt that only answers
  // product questions and politely deflects anything personalized.
  const isAnon = !user;
  const quickPrompts = isAnon ? ANON_QUICK_PROMPTS : AUTH_QUICK_PROMPTS;

  const send = async (text) => {
    const msg = (text ?? draft).trim();
    if (!msg || busy) return;
    setMessages((prev) => [...prev, { role: 'user', text: msg }]);
    setDraft('');
    setBusy(true);
    try {
      if (isAnon) {
        const visitorId = getOrCreateVisitorId();
        // Anon path — client holds history (last 6 turns), server is
        // stateless beyond rate-limit row. Single endpoint, no session_id.
        const recentHistory = messages.slice(-5);
        const res = await axios.post(
          `${API}/sage/public-chat`,
          { message: msg, visitor_id: visitorId, history: recentHistory },
        );
        setMessages((prev) => [...prev, { role: 'assistant', text: res.data.reply }]);
      } else {
        const res = await axios.post(
          `${API}/career-coach/chat`,
          { message: msg, session_id: sessionId, source: 'sage_widget' },
          { withCredentials: true },
        );
        setSessionId(res.data.session_id);
        setMessages((prev) => [...prev, { role: 'assistant', text: res.data.reply }]);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403 && detail?.code === 'feature_limit_reached') {
        const limitMsg = `You've used all ${detail.limit} free Sage messages today. Upgrade to Pro for unlimited coaching — ascendforyou.net/pricing`;
        setMessages((prev) => [...prev, { role: 'assistant', text: limitMsg }]);
      } else {
        const text = (status === 429 && detail)
          ? (typeof detail === 'string' ? detail : detail.message || 'Daily limit reached.')
          : 'Sage is unavailable for a moment. Try again or open the full page.';
        setMessages((prev) => [...prev, { role: 'assistant', text }]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="sage-fab"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.18 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 py-3 bg-[#0f1115] text-white rounded-full shadow-lg hover:bg-[#002fa7] transition-colors"
            data-testid="sage-widget-launcher"
            aria-label="Ask Sage — your AI career copilot"
          >
            <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#002fa7]">
              <Sparkles size={14} className="text-white" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#10b981] ring-2 ring-[#0f1115]" />
            </span>
            <span className="text-sm font-semibold">
              {!user ? 'Ask about Ascend' : 'Ask Sage'}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="sage-panel"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            className="fixed bottom-6 right-6 z-40 w-[min(92vw,380px)] h-[min(80vh,540px)] bg-white border border-[#e5e7eb] rounded-lg shadow-2xl flex flex-col overflow-hidden"
            data-testid="sage-widget-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e7eb] bg-[#0f1115] text-white">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#002fa7]">
                  <Sparkles size={14} className="text-white" />
                </span>
                <div>
                  <div className="text-sm font-bold leading-none flex items-center gap-2">
                    Sage
                    {isAnon && (
                      <span
                        className="text-[9px] uppercase tracking-[0.1em] font-bold bg-[#fef3c7] text-[#92400e] px-1.5 py-0.5 rounded"
                        data-testid="sage-widget-anon-badge"
                      >
                        Free preview
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-white/60 mt-0.5">
                    {isAnon ? 'Ask me about Ascend' : 'Honest. Always-on.'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/* For anon: full button goes to /register (signup). For
                    auth users: opens /coach (full conversation page). */}
                <Link
                  to={isAnon ? '/register' : '/coach'}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                  title={isAnon ? 'Sign up free for the full Sage' : 'Open full conversation'}
                  data-testid="sage-widget-expand"
                >
                  <Maximize2 size={14} />
                </Link>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                  data-testid="sage-widget-close"
                  aria-label="Close Sage"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-[#f9fafb]" data-testid="sage-widget-messages">
              {messages.length === 0 ? (
                <div className="text-center px-2 py-6">
                  <MessageCircle size={28} className="text-[#002fa7]/30 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-[#0f1115]">
                    {isAnon ? "Hi, I'm Sage." : "Hey, I'm Sage."}
                  </p>
                  <p className="text-xs text-[#6b7280] mt-1 mb-4">
                    {isAnon
                      ? 'Ask me about Ascend — features, pricing, how it works. Sign up free for personalized resume help.'
                      : 'I already know your resume. Pick a starter or type below.'}
                  </p>
                  <div className="space-y-2">
                    {quickPrompts.map((p) => (
                      <button
                        key={p}
                        onClick={() => send(p)}
                        className="w-full text-left px-3 py-2 text-xs border border-[#e5e7eb] bg-white hover:border-[#002fa7] hover:bg-[#002fa7]/5 transition-colors text-[#4b5563] rounded"
                        data-testid={`sage-widget-prompt-${p.length}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} mb-2`}
                    >
                      <div
                        className={`max-w-[85%] px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap rounded ${
                          m.role === 'user'
                            ? 'bg-[#002fa7] text-white'
                            : 'bg-white border border-[#e5e7eb] text-[#0f1115]'
                        }`}
                        data-testid={`sage-widget-msg-${m.role}`}
                      >
                        {m.text}
                      </div>
                    </div>
                  ))}
                  {busy && (
                    <div className="flex justify-start" data-testid="sage-widget-thinking">
                      <div className="bg-white border border-[#e5e7eb] px-3 py-2 text-xs text-[#6b7280] inline-flex items-center gap-2 rounded">
                        <Loader2 size={12} className="animate-spin" /> Sage is thinking…
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-[#e5e7eb] p-3 flex gap-2 bg-white">
              <input
                ref={inputRef}
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder="Ask Sage…"
                disabled={busy}
                className="flex-1 px-3 py-2 border border-[#e5e7eb] text-xs focus:outline-none focus:border-[#002fa7] rounded"
                data-testid="sage-widget-input"
              />
              <button
                onClick={() => send()}
                disabled={busy || !draft.trim()}
                className="px-3 py-2 bg-[#002fa7] text-white hover:bg-[#001d6a] transition-colors disabled:opacity-50 rounded inline-flex items-center gap-1"
                data-testid="sage-widget-send"
              >
                <Send size={12} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SageWidget;
